import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import OpsLayout from '../../components/layout/OpsLayout';
import DeptBadge from '../../components/DeptBadge';
import ControlRoomLayout from '../../components/layout/ControlRoomLayout';
import CrShiftActions from '../../components/CrShiftActions';
import { useRoles } from '../../hooks/useRoles';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import EventDayManagePanel from '../../components/EventDayManagePanel';
import EventsListUnderCalendar from '../../components/EventsListUnderCalendar';
import MapPickerDialog from '../../components/MapPickerDialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Calendar as CalendarIcon, 
  Plus,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  MapPin,
  Trash2,
  Users,
  Search,
  Pencil,
  Briefcase,
  Coins,
  Clock,
  LogIn,
  LogOut,
  Copy,
  ArrowLeftRight
} from 'lucide-react';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, addMonths, subMonths, parseISO, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth } from 'date-fns';
import { cn } from '../../lib/utils';
import useAutoRefresh from '../../hooks/useAutoRefresh';
import AdjustShiftTimeDialog from '../../components/AdjustShiftTimeDialog';
import ManualClockDialog from '../../components/ManualClockDialog';
import AdminSwapDialog from '../../components/AdminSwapDialog';
import DayPdfButton from '../../components/DayPdfButton';
import { ClockPunchDetail } from '../../components/ClockPunchDetail';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map the free-text Role dropdown values used on the Shift Calendar onto
// the canonical `hourly_rate_breakdown` keys so payroll picks the correct
// rate for the assigned role without a separate lookup. Legacy names
// ("Dual role", "Steward (CSO)", "Event Supervisor" etc.) still resolve;
// anything else is looked up dynamically against the roles roster.
const LEGACY_ROLE_MAP = {
  'Cleaner': 'cleaner',
  'Dual role (SIA, cleaner)': 'sia_door_supervisor',
  'SIA Door Supervisor': 'sia_door_supervisor',
  'Steward (CSO)': 'cso_steward',
  'Event Supervisor': 'supervisor',
};

function roleNameToSlug(name, activeRoles = []) {
  if (!name) return null;
  if (LEGACY_ROLE_MAP[name]) return LEGACY_ROLE_MAP[name];
  const found = activeRoles.find((r) => r.name === name);
  return found ? found.slug : null;
}

// Same list as UpcomingShiftsPage — kept as a local const so both files can
// evolve independently without a circular import through /components.
const SHIFT_CURRENCY_OPTIONS = [
  { code: 'GBP', symbol: '£', label: 'GBP (£)' },
  { code: 'EUR', symbol: '€', label: 'EUR (€)' },
  { code: 'USD', symbol: '$', label: 'USD ($)' },
];
const shiftCurrencySymbol = (code) =>
  SHIFT_CURRENCY_OPTIONS.find((c) => c.code === code)?.symbol || '£';

export default function ShiftsPage({ category = 'event', opsMode = false, crMode = false }) {
  const Layout = crMode ? ControlRoomLayout : (opsMode ? OpsLayout : AdminLayout);
  const restricted = opsMode || crMode;
  const isRetail = category === 'retail';
  const navigate = useNavigate();
  const { activeRoles, rolesBySlug } = useRoles();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [savedSites, setSavedSites] = useState([]);
  const [siteListOpen, setSiteListOpen] = useState(false);
  const [editSiteListOpen, setEditSiteListOpen] = useState(false);

  const fetchSites = useCallback(async () => {
    if (!isRetail) return;
    try {
      const r = await axios.get(`${API}/admin/retail-sites`);
      setSavedSites(r.data || []);
    } catch { /* non-blocking */ }
  }, [isRetail]);

  useEffect(() => { fetchSites(); }, [fetchSites]);

  const saveSiteToLibrary = async (name, postcode, locationLink = null, geoRadius = null) => {
    if (!isRetail || !name?.trim()) return;
    try {
      await axios.post(`${API}/admin/retail-sites/upsert`, {
        site_name: name.trim(),
        postcode: (postcode || '').trim(),
        location_link: (locationLink || '').trim() || null,
        geo_radius: (locationLink || '').trim() && geoRadius ? parseInt(geoRadius) : null,
      });
      fetchSites();
    } catch { /* non-blocking */ }
  };

  const renderSiteSuggestions = (query, onPick, open) => {
    if (!isRetail || !open || savedSites.length === 0) return null;
    const q = (query || '').toLowerCase();
    const list = savedSites.filter((s) => !q || s.site_name.toLowerCase().includes(q));
    if (list.length === 0) return null;
    return (
      <div className="absolute z-50 left-0 right-0 top-full mt-1 max-h-48 overflow-y-auto border border-border rounded-sm bg-popover shadow-lg" data-testid="site-suggestions">
        {list.map((s) => (
          <button
            key={s.id}
            type="button"
            onMouseDown={(e) => { e.preventDefault(); onPick(s); }}
            className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center justify-between gap-2"
            data-testid={`site-option-${s.id}`}
          >
            <span className="truncate">{s.site_name}</span>
            <span className="flex items-center gap-1.5 shrink-0">
              {s.location_link && <MapPin className="w-3 h-3 text-emerald-400" title="Saved clock-in location" />}
              <span className="text-xs text-muted-foreground">{s.postcode}</span>
            </span>
          </button>
        ))}
      </div>
    );
  };
  const [editOpen, setEditOpen] = useState(false);
  const [createMapOpen, setCreateMapOpen] = useState(false);
  const [editMapOpen, setEditMapOpen] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  // State for the Admin Time Adjustment dialog. `adjusting` holds the row
  // to adjust; when non-null the dialog opens in 'shift' scope so admin
  // can move the actual clock-in / clock-out for a completed shift.
  const [adjusting, setAdjusting] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, shift: null });
  const [deletePunch, setDeletePunch] = useState({ open: false, event: null, shiftName: '' });
  const [swapDialog, setSwapDialog] = useState({ open: false, shift: null });
  const [creating, setCreating] = useState(false);
  const [openShiftEvents, setOpenShiftEvents] = useState([]);
  const [clientJobs, setClientJobs] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [dayClockEvents, setDayClockEvents] = useState([]);
  const [manualClock, setManualClock] = useState({ open: false, employee: null, action: undefined, shiftId: null });
  const [photoDialog, setPhotoDialog] = useState({ open: false, url: null });
  const [expandedShifts, setExpandedShifts] = useState(new Set());

  const toggleShiftExpand = (id) => {
    setExpandedShifts((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const statusDot = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500';
      case 'in_progress': return 'bg-yellow-400';
      case 'missed': return 'bg-red-500';
      default: return 'bg-blue-400';
    }
  };
  
  // Form state
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);
  const [datePickerMonth, setDatePickerMonth] = useState(new Date());
  const [staffSearch, setStaffSearch] = useState('');
  const [shiftForm, setShiftForm] = useState({
    start_time: '09:00',
    end_time: '17:00',
    site_name: '',
    postcode: '',
    shift_role: '',
    shift_category: category,
    // Per-role hourly rate breakdown (£/hr). All four roles remembered on
    // the shift so payroll picks the correct rate when the employee's actual
    // role is known. Same keys as Upcoming Shifts' `hourly_rate_breakdown`.
    hourly_rate_breakdown: { sia_door_supervisor: 0, cso_steward: 0, cleaner: 0, supervisor: 0 },
    // ISO 4217 currency (£/€/$). Drives the symbol shown next to the rate.
    currency: 'GBP',
    // Free-text rate the admin types; converted into the correct role slot
    // on the breakdown at save time (auto-picks the key from `shift_role`).
    hourly_rate: ''
  });

  const [editForm, setEditForm] = useState({
    start_time: '',
    end_time: '',
    site_name: '',
    postcode: '',
    notes: '',
    shift_role: '',
    shift_category: 'event',
    hourly_rate_breakdown: { sia_door_supervisor: 0, cso_steward: 0, cleaner: 0, supervisor: 0 },
    currency: 'GBP',
    hourly_rate: ''
  });

  const fetchData = useCallback(async () => {
    const start = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
    const end = format(endOfMonth(currentMonth), 'yyyy-MM-dd');
    
    try {
      const [shiftsRes, empRes, supRes, eventsRes] = await Promise.all([
        axios.get(`${API}/shifts/calendar`, { params: { start_date: start, end_date: end } }),
        axios.get(`${API}${restricted ? '/ops/employees' : '/admin/employees'}`),
        axios.get(`${API}/admin/supervisors`),
        axios.get(`${API}/open-shifts`)
      ]);
      setShifts(shiftsRes.data);
      setEmployees(empRes.data);
      setSupervisors(supRes.data);
      setClientJobs((eventsRes.data || []).filter(s => s.source === 'client_request'));
      
      // Group open shifts into events
      const groups = {};
      eventsRes.data.forEach(s => {
        const key = `${s.position || ''}|${s.location || ''}|${s.start_time || ''}|${s.end_time || ''}`;
        if (!groups[key]) {
          groups[key] = {
            key,
            position: s.position || '',
            location: s.location || '',
            start_time: s.start_time || '',
            end_time: s.end_time || '',
            dates: [],
            shift_ids: [],
            dress_code: s.dress_code || '',
            shift_category: s.shift_category || 'event',
            shifts: []
          };
        }
        groups[key].dates.push(s.date);
        groups[key].shift_ids.push(s.id);
        groups[key].shifts.push(s);
      });
      setOpenShiftEvents(Object.values(groups));
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load shifts');
    } finally {
      setLoading(false);
    }
  }, [currentMonth]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Event Calendar only: keep all admins/secondary admins in sync without
  // manual refresh — including the day's clock punches, otherwise a fresh
  // clock-in stays invisible until the admin re-selects the day.
  useAutoRefresh(() => { fetchData(); fetchDayClock(); }, { enabled: !isRetail });

  // Clock punches for the selected day (photos + GPS shown per shift)
  const fetchDayClock = useCallback(async () => {
    // Operations has NO access to clock in/out data.
    if (restricted || !selectedDate) { setDayClockEvents([]); return; }
    try {
      const r = await axios.get(`${API}/admin/clock/events-by-date`, {
        params: { date: format(selectedDate, 'yyyy-MM-dd') },
      });
      setDayClockEvents(r.data || []);
    } catch {
      setDayClockEvents([]);
    }
  }, [selectedDate, restricted]);

  useEffect(() => { fetchDayClock(); }, [fetchDayClock]);

  const punchesFor = (shift) => {
    const evs = dayClockEvents.filter(
      (e) => e.employee_id === shift.employee_id && (e.shift_id === shift.id || !e.shift_id)
    );
    const ins = evs.filter((e) => e.event_type === 'clock_in');
    const outs = evs.filter((e) => e.event_type === 'clock_out');
    const inEv = ins.find((e) => e.shift_id === shift.id) || ins[0] || null;
    const outEv = [...outs].reverse().find((e) => e.shift_id === shift.id) || outs[outs.length - 1] || null;
    return { inEv, outEv };
  };

  const viewPhoto = async (eventId) => {
    try {
      const res = await axios.get(`${API}/admin/clock/photo/${eventId}`);
      if (res.data.photo_url && res.data.photo_url !== 'has_photo') {
        setPhotoDialog({ open: true, url: res.data.photo_url });
      } else {
        toast.info('No photo available for this event');
      }
    } catch {
      toast.error('Failed to load photo');
    }
  };

  const handleQuickStatus = async (shift, status) => {
    if (shift.status === status) return;
    try {
      await axios.put(`${API}/shifts/${shift.id}`, { status });
      toast.success(`Status set to ${status.replace('_', ' ')}`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update status');
    }
  };

  const confirmDeletePunch = async () => {
    if (!deletePunch.event) return;
    const kind = deletePunch.event.event_type === 'clock_out' ? 'Clock out' : 'Clock in';
    try {
      await axios.delete(`${API}/admin/clock/${deletePunch.event.id}`);
      toast.success(`${kind} deleted — previous state restored`);
      setDeletePunch({ open: false, event: null, shiftName: '' });
      fetchData();
      fetchDayClock();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to delete ${kind.toLowerCase()}`);
    }
  };

  const openManualClock = (shift, action) => {
    const staff = [...employees, ...supervisors].find((u) => u.id === shift.employee_id);
    setManualClock({
      open: true,
      employee: {
        id: shift.employee_id,
        name: shift.employee_name,
        is_on_shift: staff ? !!staff.is_on_shift : action === 'out',
      },
      action,
      shiftId: shift.id,
    });
  };

  // Generate dates between start and end - no longer needed, using selectedDates directly
  const toggleDateSelection = (dateStr) => {
    setSelectedDates(prev => 
      prev.includes(dateStr)
        ? prev.filter(d => d !== dateStr)
        : [...prev, dateStr].sort()
    );
  };

  // Get calendar days for the date picker
  const getDatePickerDays = () => {
    const monthStart = startOfMonth(datePickerMonth);
    const monthEnd = endOfMonth(datePickerMonth);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  };

  const handleCreateShifts = async (e) => {
    e.preventDefault();
    
    if (selectedEmployees.length === 0) {
      toast.error('Please select at least one employee');
      return;
    }
    
    if (selectedDates.length === 0) {
      toast.error('Please select at least one date');
      return;
    }
    
    setCreating(true);
    
    if (selectedEvent) {
      // Event-linked: use bulk-add-staff to add employees to all event days
      let successCount = 0;
      let errorCount = 0;
      for (const employeeId of selectedEmployees) {
        try {
          await axios.post(`${API}/open-shifts/bulk-add-staff`, {
            user_id: employeeId,
            shift_ids: selectedEvent.shift_ids
          });
          successCount++;
        } catch (error) {
          errorCount++;
        }
      }
      if (successCount > 0) {
        toast.success(`Added ${successCount} staff to event (${selectedEvent.dates.length} day${selectedEvent.dates.length > 1 ? 's' : ''} each)`);
      }
      if (errorCount > 0) {
        toast.error(`Failed to add ${errorCount} staff (may already be assigned)`);
      }
    } else {
      // Standalone shift creation — use the batch endpoint so each employee
      // receives ONE summary email covering all the selected dates, instead
      // of one email per date.
      try {
        // When admin typed a rate, fold it into the breakdown slot that
        // matches the assigned role. If no role is picked yet, we still
        // ship the raw breakdown so the value isn't silently dropped.
        const roleKey = roleNameToSlug(shiftForm.shift_role, activeRoles);
        const typedRate = shiftForm.hourly_rate === ''
          ? 0
          : Math.max(0, parseFloat(shiftForm.hourly_rate) || 0);
        const rateBd = { ...shiftForm.hourly_rate_breakdown };
        if (roleKey) rateBd[roleKey] = typedRate;
        const res = await axios.post(`${API}/shifts/batch-standalone?notify=${window.confirm('Send email notification to the affected staff?\n\nOK = send email now\nCancel = save WITHOUT sending any email')}`, {
          employee_ids: selectedEmployees,
          dates: selectedDates,
          start_time: shiftForm.start_time,
          end_time: shiftForm.end_time,
          site_name: shiftForm.site_name,
          postcode: shiftForm.postcode,
          shift_role: shiftForm.shift_role,
          shift_category: shiftForm.shift_category || 'event',
          hourly_rate_breakdown: rateBd,
          currency: shiftForm.currency || 'GBP',
          location_link: shiftForm.location_link || null,
          geo_radius: shiftForm.location_link && shiftForm.geo_radius ? parseInt(shiftForm.geo_radius) : null,
          notes: '',
        });
        const c = res.data?.created || 0;
        const s = res.data?.skipped || 0;
        const e = res.data?.emails_sent || 0;
        if (c > 0) {
          toast.success(`Created ${c} shift(s)${e ? ` · ${e} email(s) sent` : ''}`);
          saveSiteToLibrary(shiftForm.site_name, shiftForm.postcode, shiftForm.location_link, shiftForm.geo_radius);
        }
        if (s > 0) {
          const details = res.data?.skip_details || [];
          toast.warning(
            details.length
              ? `Skipped ${s}: ${details.slice(0, 3).join(' · ')}`
              : `Skipped ${s} (overlap or staff missing)`,
            { duration: 12000 }
          );
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to create shifts');
      }
    }
    
    setCreating(false);
    setCreateOpen(false);
    resetForm();
    fetchData();
  };

  const resetForm = () => {
    setSelectedEmployees([]);
    setSelectedDates([]);
    setDatePickerMonth(new Date());
    setStaffSearch('');
    setSelectedEvent(null);
    setShiftForm({
      start_time: '09:00',
      end_time: '17:00',
      site_name: '',
      postcode: '',
      shift_role: '',
      shift_category: category,
      hourly_rate_breakdown: { sia_door_supervisor: 0, cso_steward: 0, cleaner: 0, supervisor: 0 },
      currency: 'GBP',
      hourly_rate: ''
    });
  };

  const handleEventSelect = (eventKey) => {
    if (eventKey === 'none') {
      setSelectedEvent(null);
      setSelectedDates([]);
      setShiftForm({
        start_time: '09:00', end_time: '17:00', site_name: '', postcode: '',
        shift_role: '', shift_category: category,
        hourly_rate_breakdown: { sia_door_supervisor: 0, cso_steward: 0, cleaner: 0, supervisor: 0 },
        currency: 'GBP', hourly_rate: ''
      });
      return;
    }
    const event = openShiftEvents.find(e => e.key === eventKey);
    if (!event) return;
    setSelectedEvent(event);
    setSelectedDates([...event.dates].sort());
    setShiftForm({
      start_time: event.start_time,
      end_time: event.end_time,
      site_name: event.location,
      postcode: '',
      shift_role: '',
      shift_category: event.shift_category || 'event',
      hourly_rate_breakdown: {
        sia_door_supervisor: Number(event.shifts?.[0]?.hourly_rate_breakdown?.sia_door_supervisor) || 0,
        cso_steward: Number(event.shifts?.[0]?.hourly_rate_breakdown?.cso_steward) || 0,
        cleaner: Number(event.shifts?.[0]?.hourly_rate_breakdown?.cleaner) || 0,
        supervisor: Number(event.shifts?.[0]?.hourly_rate_breakdown?.supervisor) || 0,
      },
      currency: event.shifts?.[0]?.currency || 'GBP',
      hourly_rate: ''
    });
  };

  const openEditDialog = (shift) => {
    setEditingShift(shift);
    const rateKey = roleNameToSlug(shift.shift_role || '', activeRoles);
    const rateBd = {
      sia_door_supervisor: Number(shift.hourly_rate_breakdown?.sia_door_supervisor) || 0,
      cso_steward: Number(shift.hourly_rate_breakdown?.cso_steward) || 0,
      cleaner: Number(shift.hourly_rate_breakdown?.cleaner) || 0,
      supervisor: Number(shift.hourly_rate_breakdown?.supervisor) || 0,
    };
    setEditForm({
      start_time: shift.start_time || '',
      end_time: shift.end_time || '',
      site_name: shift.site_name || '',
      postcode: shift.postcode || '',
      notes: shift.notes || '',
      shift_role: shift.shift_role || '',
      shift_category: shift.shift_category || 'event',
      hourly_rate_breakdown: rateBd,
      currency: shift.currency || 'GBP',
      // Pre-fill the visible rate input from the slot matching the current
      // role so admin sees exactly what will be saved when they press Save.
      hourly_rate: rateKey && rateBd[rateKey] > 0 ? String(rateBd[rateKey]) : '',
      location_link: shift.location_link || '',
      geo_radius: shift.geo_radius != null ? String(shift.geo_radius) : ''
    });
    setEditOpen(true);
  };

  const handleUpdateShift = async (e) => {
    e.preventDefault();
    if (!editingShift) return;
    
    setUpdating(true);
    try {
      // Fold the typed rate into the correct role slot before saving.
      const roleKey = roleNameToSlug(editForm.shift_role, activeRoles);
      const typedRate = editForm.hourly_rate === ''
        ? 0
        : Math.max(0, parseFloat(editForm.hourly_rate) || 0);
      const rateBd = { ...editForm.hourly_rate_breakdown };
      if (roleKey) rateBd[roleKey] = typedRate;
      const payload = {
        start_time: editForm.start_time,
        end_time: editForm.end_time,
        site_name: editForm.site_name,
        postcode: editForm.postcode,
        notes: editForm.notes,
        shift_role: editForm.shift_role,
        shift_category: editForm.shift_category,
        hourly_rate_breakdown: rateBd,
        currency: editForm.currency || 'GBP',
        location_link: (editForm.location_link || '').trim(),
        geo_radius: (editForm.location_link || '').trim() && editForm.geo_radius ? parseInt(editForm.geo_radius) : null,
      };
      const notify = window.confirm('Send email notification to the affected staff?\n\nOK = send email now\nCancel = save WITHOUT sending any email');
      await axios.put(`${API}/shifts/${editingShift.id}?notify=${notify}`, payload);
      toast.success(notify ? 'Shift updated — email sent' : 'Shift updated (no email sent)');
      saveSiteToLibrary(editForm.site_name, editForm.postcode, editForm.location_link, editForm.geo_radius);
      setEditOpen(false);
      setEditingShift(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update shift');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteShift = async () => {
    if (!deleteDialog.shift) return;
    
    try {
      if (opsMode) {
        await axios.post(`${API}/ops/deletion-requests`, { target_type: 'shift', target_id: deleteDialog.shift.id });
        toast.success('Deletion request sent to the Primary Admin for approval');
        setDeleteDialog({ open: false, shift: null });
        return;
      }
      await axios.delete(`${API}/shifts/${deleteDialog.shift.id}`);
      toast.success('Shift deleted successfully');
      setDeleteDialog({ open: false, shift: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete shift');
    }
  };

  const openCreateDialog = () => {
    // Pre-select the currently selected date on the calendar
    setSelectedDates([format(selectedDate, 'yyyy-MM-dd')]);
    setDatePickerMonth(selectedDate);
    setCreateOpen(true);
  };

  const toggleEmployee = (employeeId) => {
    setSelectedEmployees(prev => 
      prev.includes(employeeId) 
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  // Combine employees and supervisors for shift assignment
  const allStaff = [
    ...supervisors.map(s => ({ ...s, role: 'supervisor' })),
    ...employees.map(e => ({ ...e, role: 'employee' }))
  ];

  // Filter staff by search term
  const filteredStaff = allStaff.filter(emp =>
    emp.name.toLowerCase().includes(staffSearch.toLowerCase())
  );

  const selectAllEmployees = () => {
    if (selectedEmployees.length === allStaff.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(allStaff.map(e => e.id));
    }
  };

  const getShiftsForDate = (date) => {
    return shifts
      .filter(shift =>
        shift.date === format(date, 'yyyy-MM-dd') && categoryOf(shift) === category
      )
      .sort((a, b) => {
        // Staff ordered by shift start time ascending; "To be confirmed"
        // (no times) rows go last; same time → alphabetical.
        const ta = a.start_time || '99:99';
        const tb = b.start_time || '99:99';
        if (ta !== tb) return ta.localeCompare(tb);
        return (a.employee_name || '').localeCompare(b.employee_name || '');
      });
  };

  const selectedDateShifts = getShiftsForDate(selectedDate);

  // ALL categories for the selected day — the day panel shows retail AND
  // event sections so shifts can never hide in the "other" calendar.
  const allSelectedDateShifts = shifts
    .filter((s) => s.date === format(selectedDate, 'yyyy-MM-dd'))
    .sort((a, b) => {
      const ta = a.start_time || '99:99';
      const tb = b.start_time || '99:99';
      if (ta !== tb) return ta.localeCompare(tb);
      return (a.employee_name || '').localeCompare(b.employee_name || '');
    });

  // Format a shift's created_at ISO string as "12 Jun 2026, 14:35" (UK time).
  const formatCreatedAt = (iso) => {
    if (!iso) return null;
    try {
      return format(parseISO(iso), 'dd MMM yyyy, HH:mm');
    } catch {
      return null;
    }
  };

  // Normalise a shift's category to "retail" or "event" — SAME heuristic as
  // the backend clock-history classifier, so legacy rows without a
  // shift_category (e.g. older months) appear on the correct calendar.
  function categoryOf(shift) {
    const c = (shift?.shift_category || '').toString().toLowerCase().trim();
    if (c === 'retail') return 'retail';
    if (['event', 'events', 'labour', 'security'].includes(c)) return 'event';
    if (shift?.from_open_shift || shift?.open_shift_id || shift?.location) return 'event';
    if (shift?.site_name) return 'retail';
    return 'event';
  }
  const retailShiftsToday = allSelectedDateShifts.filter(s => categoryOf(s) === 'retail');
  const eventShiftsToday = allSelectedDateShifts.filter(s => categoryOf(s) === 'event');
  // Per-day counts on the monthly grid, used to colour the dot under the day
  // number so admins can see at a glance which days have retail vs event mix.
  const countsForDate = (date) => {
    const list = getShiftsForDate(date);
    return {
      retail: list.filter(s => categoryOf(s) === 'retail').length,
      event: list.filter(s => categoryOf(s) === 'event').length,
    };
  };

  // Copy the selected day's staff + times to the clipboard so admin can
  // paste the day's rota anywhere outside the app. On the Event calendar the
  // day may span multiple sites, so shifts are grouped under site headings.
  const copyDayToClipboard = async () => {
    const lines = [format(selectedDate, 'dd/MM/yyyy')];
    const fmtLine = (s) => {
      const times = s.start_time && s.end_time
        ? `${s.start_time} - ${s.end_time}`
        : 'To be confirmed';
      return `${s.employee_name}: ${times}`;
    };
    if (isRetail) {
      // Group by store/location — a day can span 2, 3…N retail locations.
      const bySite = {};
      selectedDateShifts.forEach((s) => {
        const site = s.site_name || 'Unassigned location';
        (bySite[site] = bySite[site] || []).push(s);
      });
      Object.entries(bySite).forEach(([site, list]) => {
        lines.push('', site.toUpperCase());
        list.forEach((s) => lines.push(fmtLine(s)));
      });
    } else {
      const bySite = {};
      selectedDateShifts.forEach((s) => {
        const site = s.site_name || 'Unassigned site';
        (bySite[site] = bySite[site] || []).push(s);
      });
      Object.entries(bySite).forEach(([site, list]) => {
        lines.push('', site.toUpperCase());
        list.forEach((s) => lines.push(fmtLine(s)));
      });
    }
    const text = lines.join('\n');
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success(`Copied ${selectedDateShifts.length} shift${selectedDateShifts.length === 1 ? '' : 's'} for ${format(selectedDate, 'dd/MM/yyyy')}`);
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };

  // Client-portal jobs (open_shifts with source=client_request) shown as
  // slots on the calendar — no employee names until someone claims.
  const clientJobsForDate = (date) =>
    isRetail ? [] : clientJobs.filter(j => j.date === format(date, 'yyyy-MM-dd'));
  const clientJobsToday = clientJobsForDate(selectedDate);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'status-active';
      case 'in_progress': return 'bg-primary/20 text-primary border border-primary/30';
      default: return 'status-inactive';
    }
  };

  // Calculate number of shifts to be created
  const calculateShiftCount = () => {
    if (selectedDates.length === 0 || selectedEmployees.length === 0) return 0;
    return selectedEmployees.length * selectedDates.length;
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center min-h-screen">
          <div className="animate-pulse text-muted-foreground">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 sm:p-6 space-y-6 min-h-screen">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border pb-6">
          <div>
            <h1 className="text-2xl font-barlow tracking-widest text-foreground flex items-center gap-3">
              <CalendarIcon className="w-6 h-6 text-primary" strokeWidth={1.5} />
              {isRetail ? 'RETAIL CALENDAR' : 'EVENT CALENDAR'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {isRetail ? 'Manage and schedule retail shifts' : 'Manage and schedule event / festival shifts'}
            </p>
          </div>
          
          {crMode ? null : isRetail ? (
            <Button 
              onClick={openCreateDialog}
              data-testid="create-shift-btn"
              className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm uppercase tracking-wider btn-glow"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Shift
            </Button>
          ) : (
            <Button 
              onClick={() => navigate(opsMode ? '/employee/ops/events-manager?create=1' : '/admin/events/create')}
              data-testid="add-event-btn"
              className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm uppercase tracking-wider btn-glow"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Event
            </Button>
          )}
        </div>

        {/* Events list ABOVE the calendar — click opens the event's
            dedicated full-management view */}
        {!isRetail && !crMode && (
          <EventsListUnderCalendar monthDate={currentMonth} />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar */}
          <Card className="lg:col-span-2 bg-card border-border rounded-sm">
            <CardHeader className="border-b border-border pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-barlow tracking-wider uppercase">
                  {format(currentMonth, 'MMMM yyyy')}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                    className="h-8 w-8 border-border rounded-sm"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                    className="h-8 w-8 border-border rounded-sm"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              {/* Custom Calendar with White bg, Black text, Red for shifts, Yellow for selected */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                  <div key={day} className="text-center text-xs text-muted-foreground py-2 font-medium">
                    {day}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {/* Empty cells for days before month starts */}
                {Array.from({ length: startOfMonth(currentMonth).getDay() }).map((_, i) => (
                  <div key={`empty-${i}`} className="aspect-square" />
                ))}
                {eachDayOfInterval({
                  start: startOfMonth(currentMonth),
                  end: endOfMonth(currentMonth)
                }).map(day => {
                  const dayShifts = getShiftsForDate(day);
                  const hasShifts = dayShifts.length > 0;
                  const clientCount = clientJobsForDate(day).length;
                  const isSelected = format(day, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd');
                  const counts = countsForDate(day);

                  return (
                    <button
                      key={day.toISOString()}
                      onClick={() => setSelectedDate(day)}
                      className={cn(
                        "aspect-square flex flex-col items-center justify-center rounded-sm transition-all text-sm font-medium relative",
                        // Base: white background, black text
                        "bg-white text-black",
                        // Yellow when selected (priority over red)
                        isSelected && "!bg-yellow-400 !text-black ring-2 ring-yellow-500",
                        // Red when has shifts (but not if selected)
                        hasShifts && !isSelected && "!bg-red-500 !text-foreground",
                        // Purple when only client-request jobs exist
                        clientCount > 0 && !hasShifts && !isSelected && "!bg-purple-600 !text-white",
                        // Hover effect
                        !isSelected && !hasShifts && clientCount === 0 && "hover:bg-accent"
                      )}
                      data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
                    >
                      <span>{format(day, 'd')}</span>
                      {(hasShifts || clientCount > 0) && (
                        <span className="flex items-center gap-0.5 mt-0.5 text-[9px] font-bold leading-none">
                          {counts.retail > 0 && (
                            <span
                              className="inline-flex items-center px-1 rounded-sm bg-blue-600 text-white"
                              title={`${counts.retail} retail`}
                              data-testid={`day-retail-${format(day, 'yyyy-MM-dd')}`}
                            >
                              R·{counts.retail}
                            </span>
                          )}
                          {counts.event > 0 && (
                            <span
                              className="inline-flex items-center px-1 rounded-sm bg-orange-600 text-white"
                              title={`${counts.event} event/festival`}
                              data-testid={`day-event-${format(day, 'yyyy-MM-dd')}`}
                            >
                              E·{counts.event}
                            </span>
                          )}
                          {clientCount > 0 && (
                            <span
                              className="inline-flex items-center px-1 rounded-sm bg-purple-700 text-white"
                              title={`${clientCount} client request job(s)`}
                              data-testid={`day-client-${format(day, 'yyyy-MM-dd')}`}
                            >
                              C·{clientCount}
                            </span>
                          )}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Event day management — full Upcoming Shifts powers in-calendar */}
          {!isRetail && (
            <EventDayManagePanel
              dateStr={format(selectedDate, 'yyyy-MM-dd')}
              onChanged={fetchData}
            />
          )}

          {/* Selected Date Shifts — full width under the calendar on event
              pages so every staff row shows the complete name and line */}
          <Card className={cn('bg-card border-border rounded-sm', !isRetail && 'lg:col-span-3')}>
            <CardHeader className="border-b border-border pb-4">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-sm font-barlow tracking-wider uppercase">
                  {format(selectedDate, 'EEEE, MMM d')}
                </CardTitle>
                {selectedDateShifts.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyDayToClipboard}
                    data-testid="copy-day-btn"
                    className="h-7 px-2 rounded-sm text-[10px] uppercase tracking-wider border-primary/40 text-primary hover:bg-primary/10"
                    title="Copy this day's staff and times to clipboard"
                  >
                    <Copy className="w-3 h-3 mr-1" /> Copy
                  </Button>
                )}
              </div>
              {allSelectedDateShifts.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  {retailShiftsToday.length > 0 && (
                    <Badge
                      className="text-[10px] uppercase tracking-wider bg-blue-500/15 text-blue-300 border border-blue-500/40"
                      data-testid="day-retail-count"
                    >
                      Retail · {retailShiftsToday.length}
                    </Badge>
                  )}
                  {eventShiftsToday.length > 0 && (
                    <Badge
                      className="text-[10px] uppercase tracking-wider bg-orange-500/15 text-orange-300 border border-orange-500/40"
                      data-testid="day-event-count"
                    >
                      Event/Festival · {eventShiftsToday.length}
                    </Badge>
                  )}
                </div>
              )}
              {clientJobsToday.length > 0 && (
                <Badge
                  className="text-[10px] uppercase tracking-wider bg-purple-500/15 text-purple-300 border border-purple-500/40 mt-2"
                  data-testid="day-client-count"
                >
                  Client Requests · {clientJobsToday.length}
                </Badge>
              )}
            </CardHeader>
            <CardContent className="p-0">
              {clientJobsToday.length > 0 && (
                <div data-testid="client-jobs-section">
                  <div className="px-4 py-2 border-y text-[11px] font-barlow tracking-widest uppercase flex items-center justify-between bg-purple-500/10 text-purple-300 border-purple-500/30">
                    <span>Client Requests</span>
                    <span className="font-mono opacity-80">{clientJobsToday.length}</span>
                  </div>
                  <div className="divide-y divide-border">
                    {clientJobsToday.map((job) => (
                      <div key={job.id} className="p-4 hover:bg-accent/30 transition-colors" data-testid={`client-job-${job.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{job.company_name || 'Client'}</p>
                            <p className="text-xs text-muted-foreground font-mono mt-0.5">{job.start_time} - {job.end_time}</p>
                          </div>
                          <Badge
                            className={cn(
                              "text-[9px] uppercase tracking-wider border shrink-0",
                              (job.claimed_count || 0) >= (job.quantity || 1)
                                ? "bg-green-500/15 text-green-300 border-green-500/40"
                                : "bg-purple-500/15 text-purple-300 border-purple-500/40"
                            )}
                            data-testid={`client-job-progress-${job.id}`}
                          >
                            {job.claimed_count || 0}/{job.quantity || 1} staff
                          </Badge>
                        </div>
                        <p className="text-xs text-foreground mt-2">{job.position}</p>
                        <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                          <MapPin className="w-3 h-3 shrink-0" />
                          {job.location}{job.address ? ` — ${job.address}` : ''}{job.postcode ? `, ${job.postcode}` : ''}
                        </p>
                        {(job.claimed_by_list || []).length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {(job.claimed_by_list || []).map((c) => (
                              <Badge key={c.user_id} variant="outline" className="text-[10px] border-border text-foreground">
                                {c.user_name}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {allSelectedDateShifts.length > 0 ? (
                <div>
                  {[
                    {
                      key: 'retail',
                      list: retailShiftsToday,
                      label: 'Retail Staff',
                      headerCls: 'bg-blue-500/10 text-blue-300 border-blue-500/30',
                      badgeCls: 'bg-blue-500/15 text-blue-300 border-blue-500/40',
                      badgeText: 'RETAIL',
                    },
                    {
                      key: 'event',
                      list: eventShiftsToday,
                      label: 'Event / Festival Staff',
                      headerCls: 'bg-orange-500/10 text-orange-300 border-orange-500/30',
                      badgeCls: 'bg-orange-500/15 text-orange-300 border-orange-500/40',
                      badgeText: 'EVENT/FESTIVAL',
                    },
                  ].map((section) => (
                    section.list.length === 0 ? null : (
                      <div key={section.key} data-testid={`category-section-${section.key}`}>
                        <div
                          className={cn(
                            "px-4 py-2 border-y text-[11px] font-barlow tracking-widest uppercase flex items-center justify-between",
                            section.headerCls
                          )}
                        >
                          <span>{section.label}</span>
                          <span className="font-mono opacity-80">{section.list.length}</span>
                        </div>
                        <div className="divide-y divide-border">
                          {section.list.map((shift) => (
                            <div
                              key={shift.id}
                              data-testid={`shift-${shift.id}`}
                              data-category={section.key}
                              className="hover:bg-accent/30 transition-colors"
                            >
                              {/* Compact row — just the name; click to reveal the full card */}
                              <button
                                type="button"
                                onClick={() => toggleShiftExpand(shift.id)}
                                data-testid={`shift-toggle-${shift.id}`}
                                className="w-full flex items-center justify-between px-4 py-3 text-left"
                              >
                                <span className="flex items-center gap-2 min-w-0">
                                  <span className={cn('w-2 h-2 rounded-full flex-shrink-0', statusDot(shift.status))} title={shift.status.replace('_', ' ')} />
                                  <span className="text-sm font-medium text-foreground truncate">{shift.employee_name}</span>
                                  {shift.site_name && (
                                    <span className="text-[10px] text-muted-foreground truncate max-w-[140px]" title={shift.site_name} data-testid={`shift-site-${shift.id}`}>
                                      · {shift.site_name}
                                    </span>
                                  )}
                                  {shift.is_extra && (
                                    <span className="text-[9px] uppercase tracking-wider px-1 py-0.5 rounded-sm bg-amber-500/15 text-amber-300 border border-amber-500/40 flex-shrink-0">+ Extra</span>
                                  )}
                                </span>
                                <span className="flex items-center gap-2 flex-shrink-0">
                                  <span className="text-[10px] font-mono text-muted-foreground">
                                    {shift.start_time && shift.end_time
                                      ? `${shift.start_time} - ${shift.end_time}`
                                      : <span className="text-amber-400 uppercase tracking-wider">Pending</span>}
                                  </span>
                                  <ChevronDown className={cn('w-4 h-4 text-muted-foreground transition-transform duration-200', expandedShifts.has(shift.id) && 'rotate-180')} />
                                </span>
                              </button>
                              {!restricted && shift.dept_touch && (
                                <div className="px-4 -mt-1 pb-2">
                                  <DeptBadge deptTouch={shift.dept_touch} targetId={shift.id} />
                                </div>
                              )}
                              {section.key === 'retail' && formatCreatedAt(shift.created_at) && (
                                <p className="px-4 -mt-2 pb-2 text-[10px] text-muted-foreground font-mono flex items-center gap-1" data-testid={`shift-created-at-${shift.id}`}>
                                  <CalendarIcon className="w-3 h-3 opacity-60" />
                                  Created {formatCreatedAt(shift.created_at)}
                                </p>
                              )}

                              {expandedShifts.has(shift.id) && (
                              <div className="px-4 pb-4" data-testid={`shift-details-${shift.id}`}>
                              <div className="flex items-start justify-between mb-2">
                                <div>
                                  <button
                                    type="button"
                                    onClick={() => { if (!restricted) navigate(`/admin/employees/${shift.employee_id}`); }}
                                    data-testid={`open-employee-${shift.employee_id}`}
                                    className="text-sm font-medium text-foreground hover:text-primary hover:underline text-left focus:outline-none focus:text-primary cursor-pointer transition-colors"
                                    title="View clock history and shift details"
                                  >
                                    {shift.employee_name}
                                  </button>
                                  {!restricted && shift.dept_touch && (
                                    <DeptBadge deptTouch={shift.dept_touch} targetId={shift.id} className="ml-2 align-middle" />
                                  )}
                                  <p className="text-xs text-muted-foreground font-mono">
                                    {shift.start_time && shift.end_time ? (
                                      <>{shift.start_time} - {shift.end_time}</>
                                    ) : shift.start_time || shift.end_time ? (
                                      <span data-testid={`partial-times-${shift.id}`}>
                                        {shift.start_time ? (
                                          <span className="text-foreground">Start {shift.start_time}</span>
                                        ) : (
                                          <span className="text-amber-400 font-semibold uppercase tracking-wider not-italic">Start Pending</span>
                                        )}
                                        <span className="text-muted-foreground mx-1">•</span>
                                        {shift.end_time ? (
                                          <span className="text-foreground">End {shift.end_time}</span>
                                        ) : (
                                          <span className="text-amber-400 font-semibold uppercase tracking-wider not-italic">End Pending</span>
                                        )}
                                      </span>
                                    ) : (
                                      <span className="text-amber-400 font-semibold uppercase tracking-wider not-italic px-1.5 py-0.5 bg-amber-500/10 border border-amber-500/30 rounded-sm" data-testid={`pending-times-${shift.id}`}>
                                        Pending — Set Times
                                      </span>
                                    )}
                                  </p>
                                </div>
                                <div className="flex items-center gap-1">
                                  {!crMode && (
                                  <>
                                  <Badge
                                    className={cn("text-[9px] uppercase tracking-wider mr-1 border", section.badgeCls)}
                                    data-testid={`category-badge-${shift.id}`}
                                    title="Shift category"
                                  >
                                    {section.badgeText}
                                  </Badge>
                                  {shift.is_extra && (
                                    <Badge
                                      className="text-[9px] uppercase tracking-wider bg-amber-500/15 text-amber-300 border-amber-500/40 mr-1"
                                      data-testid={`extra-badge-${shift.id}`}
                                      title="Added as extra (over planned capacity)"
                                    >
                                      + Extra
                                    </Badge>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => openEditDialog(shift)}
                                    data-testid={`edit-shift-${shift.id}`}
                                    className="h-7 w-7 text-muted-foreground hover:text-primary"
                                  >
                                    <Pencil className="w-3 h-3" />
                                  </Button>
                                  {shift.status === 'scheduled' && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => setSwapDialog({ open: true, shift })}
                                      data-testid={`swap-shift-${shift.id}`}
                                      className="h-7 w-7 text-muted-foreground hover:text-primary"
                                      title="Swap this shift with another employee's shift"
                                    >
                                      <ArrowLeftRight className="w-3 h-3" />
                                    </Button>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setDeleteDialog({ open: true, shift })}
                                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                                    data-testid={`delete-shift-${shift.id}`}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                  </>
                                  )}
                                </div>
                              </div>
                              {crMode && <CrShiftActions shift={shift} onDone={fetchData} />}

                              {(() => {
                                const { inEv, outEv } = punchesFor(shift);
                                return (
                                  <>
                                    <div className="flex flex-wrap items-center gap-2">
                                      {crMode ? (
                                        <Badge className={cn("text-xs select-none", getStatusColor(shift.status))} data-testid={`status-badge-${shift.id}`}>
                                          {shift.status.replace('_', ' ')}
                                        </Badge>
                                      ) : (
                                      <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                          <button type="button" data-testid={`status-badge-${shift.id}`} title="Click to change status">
                                            <Badge className={cn("text-xs cursor-pointer select-none", getStatusColor(shift.status))}>
                                              {shift.status.replace('_', ' ')} ▾
                                            </Badge>
                                          </button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="start" className="bg-card border-border rounded-sm">
                                          {['scheduled', 'in_progress', 'completed', 'missed'].map((s) => (
                                            <DropdownMenuItem
                                              key={s}
                                              onClick={() => handleQuickStatus(shift, s)}
                                              data-testid={`status-option-${s}-${shift.id}`}
                                              className={cn('text-xs uppercase tracking-wider cursor-pointer', shift.status === s && 'text-primary')}
                                            >
                                              {s.replace('_', ' ')}
                                            </DropdownMenuItem>
                                          ))}
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                      )}
                                      {restricted ? null : !inEv ? (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => openManualClock(shift, 'in')}
                                          data-testid={`clock-in-btn-${shift.id}`}
                                          className="h-6 px-2 rounded-sm text-[10px] uppercase tracking-wider border-green-500/40 text-green-400 hover:bg-green-500/10"
                                        >
                                          <LogIn className="w-3 h-3 mr-1" /> Clock In
                                        </Button>
                                      ) : !outEv ? (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => openManualClock(shift, 'out')}
                                          data-testid={`clock-out-btn-${shift.id}`}
                                          className="h-6 px-2 rounded-sm text-[10px] uppercase tracking-wider border-red-500/40 text-red-400 hover:bg-red-500/10"
                                        >
                                          <LogOut className="w-3 h-3 mr-1" /> Clock Out
                                        </Button>
                                      ) : null}
                                    </div>

                                    {!restricted && (
                                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        <ClockPunchDetail kind="in" event={inEv} onViewPhoto={viewPhoto} />
                                        <ClockPunchDetail kind="out" event={outEv} onViewPhoto={viewPhoto} />
                                      </div>
                                    )}
                                    {!restricted && (inEv || outEv) && (
                                      <div className="mt-2">
                                        <DayPdfButton employeeId={shift.employee_id} employeeName={shift.employee_name} date={shift.date} />
                                      </div>
                                    )}
                                    {!restricted && (inEv || outEv) && (
                                      <div className="mt-2 flex flex-wrap gap-2">
                                        {outEv && (
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setDeletePunch({ open: true, event: outEv, shiftName: shift.employee_name })}
                                            data-testid={`delete-clock-out-${shift.id}`}
                                            className="h-6 px-2 rounded-sm text-[10px] uppercase tracking-wider border-red-500/40 text-red-400 hover:bg-red-500/10"
                                            title="Delete this clock out — the employee goes back On Shift"
                                          >
                                            <Trash2 className="w-3 h-3 mr-1" /> Delete Clock Out
                                          </Button>
                                        )}
                                        {inEv && !outEv && (
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setDeletePunch({ open: true, event: inEv, shiftName: shift.employee_name })}
                                            data-testid={`delete-clock-in-${shift.id}`}
                                            className="h-6 px-2 rounded-sm text-[10px] uppercase tracking-wider border-red-500/40 text-red-400 hover:bg-red-500/10"
                                            title="Delete this clock in — the shift goes back to Scheduled"
                                          >
                                            <Trash2 className="w-3 h-3 mr-1" /> Delete Clock In
                                          </Button>
                                        )}
                                      </div>
                                    )}
                                  </>
                                );
                              })()}

                              {shift.site_name && (
                                <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  {shift.site_name}
                                  {shift.postcode && ` (${shift.postcode})`}
                                </p>
                              )}
                              </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  ))}
                </div>
              ) : clientJobsToday.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <CalendarIcon className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">No shifts scheduled</p>
                  <Button
                    variant="link"
                    onClick={openCreateDialog}
                    className="text-primary mt-2"
                  >
                    Add a shift
                  </Button>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </div>

        {/* Create Shift Dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="bg-card border-border rounded-sm max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-barlow tracking-wider uppercase">
                Create Shifts
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateShifts} className="space-y-5 mt-4">

              {/* Event Selection */}
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4" />
                  Link to Event
                </Label>
                <Select value={selectedEvent?.key || 'none'} onValueChange={handleEventSelect}>
                  <SelectTrigger className="bg-background border-border rounded-sm" data-testid="event-select">
                    <SelectValue placeholder="Select an event or create standalone shift..." />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border max-h-60">
                    <SelectItem value="none">No event (standalone shift)</SelectItem>
                    {openShiftEvents.map((ev) => (
                      <SelectItem key={ev.key} value={ev.key}>
                        {ev.position} @ {ev.location} ({ev.dates.length} day{ev.dates.length > 1 ? 's' : ''})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedEvent && (
                  <p className="text-xs text-primary">
                    {selectedEvent.dates.length} day{selectedEvent.dates.length > 1 ? 's' : ''}: {selectedEvent.dates.sort().map(d => { try { return format(parseISO(d), 'MMM d'); } catch { return d; } }).join(', ')} | {selectedEvent.start_time} - {selectedEvent.end_time}
                  </p>
                )}
              </div>
              
              {/* Employee Selection */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Select Staff ({selectedEmployees.length})
                  </Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={selectAllEmployees}
                    className="text-xs text-primary h-6"
                  >
                    {selectedEmployees.length === allStaff.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>
                {/* Search Bar */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search staff..."
                    value={staffSearch}
                    onChange={(e) => setStaffSearch(e.target.value)}
                    className="pl-10 bg-background border-border rounded-sm h-9 text-sm"
                    data-testid="staff-search-input"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 bg-background rounded-sm border border-border scrollbar-thin">
                  {filteredStaff.length > 0 ? filteredStaff.map((emp) => (
                    <button
                      type="button"
                      key={emp.id}
                      className={cn(
                        "flex items-center gap-2 p-2 rounded-sm cursor-pointer transition-colors text-sm text-left",
                        selectedEmployees.includes(emp.id) 
                          ? "bg-primary/20 border border-primary text-primary" 
                          : "hover:bg-accent/50 border border-transparent"
                      )}
                      onClick={() => toggleEmployee(emp.id)}
                    >
                      <span className={cn(
                        "w-4 h-4 rounded-sm border flex items-center justify-center text-xs",
                        selectedEmployees.includes(emp.id) 
                          ? "bg-primary border-primary text-black" 
                          : "border-border"
                      )}>
                        {selectedEmployees.includes(emp.id) && "✓"}
                      </span>
                      <span className="truncate">{emp.name}</span>
                      {emp.role === 'supervisor' && (
                        <span className="text-xs text-blue-400 ml-auto">(Sup)</span>
                      )}
                    </button>
                  )) : (
                    <p className="text-sm text-muted-foreground col-span-2 text-center py-4">
                      {allStaff.length === 0 ? 'No staff. Invite employees first.' : 'No matching staff found.'}
                    </p>
                  )}
                </div>
              </div>

              {/* Date Selection - Multi-day clickable calendar */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Select Dates ({selectedDates.length} selected)
                  </Label>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setDatePickerMonth(subMonths(datePickerMonth, 1))}
                      className="h-6 w-6"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-xs font-medium min-w-[100px] text-center">
                      {format(datePickerMonth, 'MMM yyyy')}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setDatePickerMonth(addMonths(datePickerMonth, 1))}
                      className="h-6 w-6"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="bg-background border border-border rounded-sm p-2">
                  {/* Day headers */}
                  <div className="grid grid-cols-7 gap-1 mb-1">
                    {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                      <div key={day} className="text-center text-[10px] text-muted-foreground py-1">
                        {day}
                      </div>
                    ))}
                  </div>
                  {/* Calendar days */}
                  <div className="grid grid-cols-7 gap-1">
                    {getDatePickerDays().map(day => {
                      const dateStr = format(day, 'yyyy-MM-dd');
                      const isSelected = selectedDates.includes(dateStr);
                      const isCurrentMonth = isSameMonth(day, datePickerMonth);
                      const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                      
                      return (
                        <button
                          key={dateStr}
                          type="button"
                          onClick={() => toggleDateSelection(dateStr)}
                          className={cn(
                            "aspect-square flex items-center justify-center text-xs rounded-sm transition-all",
                            isSelected 
                              ? "bg-primary text-black font-bold" 
                              : "hover:bg-accent",
                            !isCurrentMonth && "opacity-30",
                            isToday && !isSelected && "border border-primary/50"
                          )}
                        >
                          {format(day, 'd')}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {selectedDates.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Selected: {selectedDates.map(d => format(parseISO(d), 'MMM d')).join(', ')}
                  </p>
                )}
              </div>
              
              {/* Time Selection — Start optional, End optional. If both
                  blank → shift is PENDING. If only Start is set, End stays
                  PENDING (admin can set later from Shift Calendar). */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Start Time <span className="text-[10px] normal-case opacity-60">(leave empty = pending)</span>
                  </Label>
                  <Input
                    type="time"
                    data-testid="start-time-input"
                    value={shiftForm.start_time}
                    onChange={(e) => setShiftForm({ ...shiftForm, start_time: e.target.value })}
                    className="bg-background border-border rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    End Time <span className="text-[10px] normal-case opacity-60">(leave empty = pending)</span>
                  </Label>
                  <Input
                    type="time"
                    data-testid="end-time-input"
                    value={shiftForm.end_time}
                    onChange={(e) => setShiftForm({ ...shiftForm, end_time: e.target.value })}
                    className="bg-background border-border rounded-sm"
                  />
                </div>
              </div>
              
              {/* Site Location */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2 relative">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Site Name
                  </Label>
                  <Input
                    data-testid="site-name-input"
                    value={shiftForm.site_name}
                    onChange={(e) => { setShiftForm({ ...shiftForm, site_name: e.target.value }); setSiteListOpen(true); }}
                    onFocus={() => setSiteListOpen(true)}
                    onBlur={() => setTimeout(() => setSiteListOpen(false), 150)}
                    autoComplete="off"
                    placeholder="e.g., Main Office"
                    className="bg-background border-border rounded-sm"
                  />
                  {renderSiteSuggestions(shiftForm.site_name, (s) => {
                    setShiftForm((f) => ({
                      ...f,
                      site_name: s.site_name,
                      postcode: s.postcode || f.postcode,
                      location_link: s.location_link || f.location_link,
                      geo_radius: s.location_link
                        ? (s.geo_radius != null ? String(s.geo_radius) : '')
                        : f.geo_radius,
                    }));
                    setSiteListOpen(false);
                  }, siteListOpen)}
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Postcode
                  </Label>
                  <Input
                    data-testid="postcode-input"
                    value={shiftForm.postcode}
                    onChange={(e) => setShiftForm({ ...shiftForm, postcode: e.target.value })}
                    placeholder="e.g., SW1A 1AA"
                    className="bg-background border-border rounded-sm"
                  />
                </div>
              </div>
              
              {shiftForm.postcode && (
                <p className="text-xs text-muted-foreground">
                  <MapPin className="w-3 h-3 inline mr-1" />
                  Employees must be within 150m to clock in/out.
                </p>
              )}

              {/* Geofenced clock-in — optional */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 border border-border rounded-sm p-3 bg-muted/40">
                <div className="space-y-2 sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Clock-in location link (optional)
                  </Label>
                  <Input
                    data-testid="retail-location-link-input"
                    value={shiftForm.location_link || ''}
                    onChange={(e) => setShiftForm({ ...shiftForm, location_link: e.target.value })}
                    placeholder="Paste Google Maps or what3words link"
                    className="bg-background border-border rounded-sm"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setCreateMapOpen(true)}
                    className="h-8 rounded-sm text-xs w-full sm:w-auto"
                    data-testid="create-pick-on-map-btn"
                  >
                    <MapPin className="w-3.5 h-3.5 mr-1.5 text-emerald-400" /> Pick on map
                  </Button>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Allowed distance</Label>
                  <select
                    data-testid="retail-geo-radius-select"
                    value={shiftForm.geo_radius || ''}
                    onChange={(e) => setShiftForm({ ...shiftForm, geo_radius: e.target.value })}
                    className="w-full h-9 bg-background border border-border rounded-sm px-2 text-sm"
                  >
                    <option value="">No limit</option>
                    <option value="25">25 m</option>
                    <option value="50">50 m</option>
                    <option value="100">100 m</option>
                    <option value="200">200 m</option>
                    <option value="500">500 m</option>
                  </select>
                  <p className="text-[10px] text-muted-foreground">Staff outside this radius CANNOT clock in.</p>
                </div>
              </div>

              {/* Shift Category — drives the Retail vs Event grouping on the calendar */}
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                  <CalendarIcon className="w-3 h-3" /> Shift Category
                </Label>
                <Select
                  value={shiftForm.shift_category}
                  onValueChange={(val) => setShiftForm({ ...shiftForm, shift_category: val })}
                >
                  <SelectTrigger className="bg-background border-border rounded-sm" data-testid="create-shift-category-select">
                    <SelectValue placeholder="Select category..." />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="retail">Retail</SelectItem>
                    <SelectItem value="event">Event / Festival</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground/70">
                  Groups staff into Retail vs Event/Festival on the Shift Calendar so the wrong category is never cancelled by mistake.
                </p>
              </div>

              {/* Shift Role */}
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                  <Briefcase className="w-3 h-3" /> Role
                </Label>
                <Select value={shiftForm.shift_role} onValueChange={(val) => {
                  // When switching role, auto-fill the visible rate: use the
                  // stored breakdown value if the admin has already typed
                  // one, otherwise fall back to the role's default_hourly_rate
                  // straight from Role Management (still overridable).
                  const key = roleNameToSlug(val, activeRoles);
                  const stored = key ? Number(shiftForm.hourly_rate_breakdown?.[key]) || 0 : 0;
                  const roleDefault = (key && rolesBySlug[key])
                    ? Number(rolesBySlug[key].default_hourly_rate) || 0
                    : 0;
                  // Also switch currency to the role's default when admin hasn't
                  // customised it on this shift yet.
                  const roleCurrency = (key && rolesBySlug[key])
                    ? rolesBySlug[key].default_currency
                    : shiftForm.currency;
                  setShiftForm({
                    ...shiftForm,
                    shift_role: val,
                    hourly_rate: stored > 0
                      ? String(stored)
                      : (roleDefault > 0 ? String(roleDefault) : ''),
                    currency: shiftForm.currency || roleCurrency || 'GBP',
                  });
                }}>
                  <SelectTrigger className="bg-background border-border rounded-sm" data-testid="create-shift-role-select">
                    <SelectValue placeholder="Select role..." />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {activeRoles.length > 0 ? (
                      activeRoles.map((r) => (
                        <SelectItem key={r.slug} value={r.name}>{r.name}</SelectItem>
                      ))
                    ) : (
                      <>
                        <SelectItem value="Cleaner">Cleaner</SelectItem>
                        <SelectItem value="SIA Door Supervisor">SIA Door Supervisor</SelectItem>
                        <SelectItem value="Steward (CSO)">Steward (CSO)</SelectItem>
                        <SelectItem value="Event Supervisor">Event Supervisor</SelectItem>
                      </>
                    )}
                    {/* Composite role kept for historical parity with older
                        shifts — maps to SIA in the rate breakdown. */}
                    <SelectItem value="Dual role (SIA, cleaner)">Dual role (SIA, cleaner)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Hourly rate for the assigned role + currency selector */}
              <div className="space-y-2 p-3 bg-muted/50 border border-border rounded-sm">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                    <Coins className="w-4 h-4" /> Hourly Rate
                  </Label>
                  <Select
                    value={shiftForm.currency || 'GBP'}
                    onValueChange={(val) => setShiftForm({ ...shiftForm, currency: val })}
                  >
                    <SelectTrigger
                      className="h-7 min-w-[110px] bg-background border-border rounded-sm text-xs"
                      data-testid="create-shift-currency-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {SHIFT_CURRENCY_OPTIONS.map((c) => (
                        <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-[80px_1fr] gap-2 items-center">
                  <span className="text-lg text-muted-foreground tabular-nums">
                    {shiftCurrencySymbol(shiftForm.currency)}/hr
                  </span>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    disabled={!roleNameToSlug(shiftForm.shift_role, activeRoles)}
                    value={shiftForm.hourly_rate ?? ''}
                    onChange={(e) => setShiftForm({ ...shiftForm, hourly_rate: e.target.value })}
                    className="bg-background border-border rounded-sm"
                    data-testid="create-shift-hourly-rate"
                  />
                </div>
                <p className="text-[11px] text-muted-foreground/70">
                  {roleNameToSlug(shiftForm.shift_role, activeRoles)
                    ? "The rate is auto-saved on the role you selected above."
                    : "Select a role above to enable the rate for that role."}
                </p>
              </div>

              {/* Summary */}
              {calculateShiftCount() > 0 && (
                <div className="p-3 bg-primary/10 border border-primary/30 rounded-sm">
                  <p className="text-sm text-center">
                    Creating <span className="font-bold text-primary">{calculateShiftCount()}</span> shift(s)
                  </p>
                </div>
              )}
              
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setCreateOpen(false); resetForm(); }}
                  className="border-border rounded-sm"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  data-testid="save-shift-btn"
                  disabled={creating || selectedEmployees.length === 0 || selectedDates.length === 0}
                  className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm"
                >
                  {creating ? 'Creating...' : 'Create Shifts'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Shift Dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="bg-card border-border rounded-sm max-w-md max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-barlow tracking-wider uppercase">
                Edit Shift
              </DialogTitle>
            </DialogHeader>
            {editingShift && (
              <form onSubmit={handleUpdateShift} className="space-y-5 mt-4">
                {/* Employee & Date Info (Read-only) */}
                <div className="p-3 bg-accent/50 rounded-sm space-y-1">
                  <p className="text-sm font-medium text-foreground">{editingShift.employee_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {format(parseISO(editingShift.date), 'EEEE, MMMM d, yyyy')}
                  </p>
                </div>

                {/* Time Selection */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      Start Time <span className="text-[10px] normal-case opacity-60">(leave empty = pending)</span>
                    </Label>
                    <Input
                      type="time"
                      data-testid="edit-start-time-input"
                      value={editForm.start_time}
                      onChange={(e) => setEditForm({ ...editForm, start_time: e.target.value })}
                      className="bg-background border-border rounded-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      End Time <span className="text-[10px] normal-case opacity-60">(leave empty = pending)</span>
                    </Label>
                    <Input
                      type="time"
                      data-testid="edit-end-time-input"
                      value={editForm.end_time}
                      onChange={(e) => setEditForm({ ...editForm, end_time: e.target.value })}
                      className="bg-background border-border rounded-sm"
                    />
                  </div>
                </div>
                
                {/* Site Location */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2 relative">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      Site Name
                    </Label>
                    <Input
                      data-testid="edit-site-name-input"
                      value={editForm.site_name}
                      onChange={(e) => { setEditForm({ ...editForm, site_name: e.target.value }); setEditSiteListOpen(true); }}
                      onFocus={() => setEditSiteListOpen(true)}
                      onBlur={() => setTimeout(() => setEditSiteListOpen(false), 150)}
                      autoComplete="off"
                      placeholder="e.g., Main Office"
                      className="bg-background border-border rounded-sm"
                    />
                    {renderSiteSuggestions(editForm.site_name, (s) => {
                      setEditForm((f) => ({
                        ...f,
                        site_name: s.site_name,
                        postcode: s.postcode || f.postcode,
                        location_link: s.location_link || f.location_link,
                        geo_radius: s.location_link
                          ? (s.geo_radius != null ? String(s.geo_radius) : '')
                          : f.geo_radius,
                      }));
                      setEditSiteListOpen(false);
                    }, editSiteListOpen)}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      Postcode
                    </Label>
                    <Input
                      data-testid="edit-postcode-input"
                      value={editForm.postcode}
                      onChange={(e) => setEditForm({ ...editForm, postcode: e.target.value })}
                      placeholder="e.g., SW1A 1AA"
                      className="bg-background border-border rounded-sm"
                    />
                  </div>
                </div>

                {/* Geofenced clock-in — optional */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 border border-border rounded-sm p-3 bg-muted/40">
                  <div className="space-y-2 sm:col-span-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      Clock-in location link (optional)
                    </Label>
                    <Input
                      data-testid="edit-location-link-input"
                      value={editForm.location_link || ''}
                      onChange={(e) => setEditForm({ ...editForm, location_link: e.target.value })}
                      placeholder="Paste Google Maps or what3words link"
                      className="bg-background border-border rounded-sm"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setEditMapOpen(true)}
                      className="h-8 rounded-sm text-xs w-full sm:w-auto"
                      data-testid="edit-pick-on-map-btn"
                    >
                      <MapPin className="w-3.5 h-3.5 mr-1.5 text-emerald-400" /> Pick on map
                    </Button>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Allowed distance</Label>
                    <select
                      data-testid="edit-geo-radius-select"
                      value={editForm.geo_radius || ''}
                      onChange={(e) => setEditForm({ ...editForm, geo_radius: e.target.value })}
                      className="w-full h-9 bg-background border border-border rounded-sm px-2 text-sm"
                    >
                      <option value="">No limit</option>
                      <option value="25">25 m</option>
                      <option value="50">50 m</option>
                      <option value="100">100 m</option>
                      <option value="200">200 m</option>
                      <option value="500">500 m</option>
                    </select>
                    <p className="text-[10px] text-muted-foreground">Staff outside this radius CANNOT clock in.</p>
                  </div>
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Notes
                  </Label>
                  <Input
                    data-testid="edit-notes-input"
                    value={editForm.notes}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                    placeholder="Optional notes..."
                    className="bg-background border-border rounded-sm"
                  />
                </div>

                {/* Shift Category */}
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                    <CalendarIcon className="w-3 h-3" /> Shift Category
                  </Label>
                  <Select
                    value={editForm.shift_category}
                    onValueChange={(val) => setEditForm({ ...editForm, shift_category: val })}
                  >
                    <SelectTrigger className="bg-background border-border rounded-sm" data-testid="edit-shift-category-select">
                      <SelectValue placeholder="Select category..." />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="retail">Retail</SelectItem>
                      <SelectItem value="event">Event / Festival</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Shift Role */}
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                    <Briefcase className="w-3 h-3" /> Role
                  </Label>
                  <Select value={editForm.shift_role} onValueChange={(val) => {
                    const key = roleNameToSlug(val, activeRoles);
                    const stored = key ? Number(editForm.hourly_rate_breakdown?.[key]) || 0 : 0;
                    const roleDefault = (key && rolesBySlug[key])
                      ? Number(rolesBySlug[key].default_hourly_rate) || 0
                      : 0;
                    setEditForm({
                      ...editForm,
                      shift_role: val,
                      hourly_rate: stored > 0
                        ? String(stored)
                        : (roleDefault > 0 ? String(roleDefault) : ''),
                    });
                  }}>
                    <SelectTrigger className="bg-background border-border rounded-sm" data-testid="edit-shift-role-select">
                      <SelectValue placeholder="Select role..." />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {activeRoles.length > 0 ? (
                        activeRoles.map((r) => (
                          <SelectItem key={r.slug} value={r.name}>{r.name}</SelectItem>
                        ))
                      ) : (
                        <>
                          <SelectItem value="Cleaner">Cleaner</SelectItem>
                          <SelectItem value="SIA Door Supervisor">SIA Door Supervisor</SelectItem>
                          <SelectItem value="Steward (CSO)">Steward (CSO)</SelectItem>
                          <SelectItem value="Event Supervisor">Event Supervisor</SelectItem>
                        </>
                      )}
                      {/* Historic composite role kept for old shifts. */}
                      <SelectItem value="Dual role (SIA, cleaner)">Dual role (SIA, cleaner)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Hourly rate for the assigned role + currency selector */}
                <div className="space-y-2 p-3 bg-muted/50 border border-border rounded-sm">
                  <div className="flex items-center justify-between gap-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                      <Coins className="w-4 h-4" /> Hourly Rate
                    </Label>
                    <Select
                      value={editForm.currency || 'GBP'}
                      onValueChange={(val) => setEditForm({ ...editForm, currency: val })}
                    >
                      <SelectTrigger
                        className="h-7 min-w-[110px] bg-background border-border rounded-sm text-xs"
                        data-testid="edit-shift-currency-select"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        {SHIFT_CURRENCY_OPTIONS.map((c) => (
                          <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-[80px_1fr] gap-2 items-center">
                    <span className="text-lg text-muted-foreground tabular-nums">
                      {shiftCurrencySymbol(editForm.currency)}/hr
                    </span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      disabled={!roleNameToSlug(editForm.shift_role, activeRoles)}
                      value={editForm.hourly_rate ?? ''}
                      onChange={(e) => setEditForm({ ...editForm, hourly_rate: e.target.value })}
                      className="bg-background border-border rounded-sm"
                      data-testid="edit-shift-hourly-rate"
                    />
                  </div>
                  <p className="text-[11px] text-muted-foreground/70">
                    {roleNameToSlug(editForm.shift_role, activeRoles)
                      ? "The rate is auto-saved on the role you selected above."
                      : "Select a role above to enable the rate for that role."}
                  </p>
                </div>
                
                {editForm.postcode && (
                  <p className="text-xs text-muted-foreground">
                    <MapPin className="w-3 h-3 inline mr-1" />
                    Employees must be within 150m to clock in/out.
                  </p>
                )}
                
                <DialogFooter className="gap-2 sm:justify-between">
                  {/* Time Adjustment quick-action for completed shifts.
                      Only surfaces on completed/in_progress rows because the
                      backend rewrites clock_events — which only exist post
                      clock-in. Scheduled/upcoming shifts already have the
                      normal Save Changes flow above. */}
                  {editingShift && ['completed', 'in_progress'].includes(editingShift.status) && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setAdjusting(editingShift)}
                      className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 rounded-sm"
                      data-testid="open-adjust-time-btn"
                    >
                      <Clock className="w-4 h-4 mr-1" /> Adjust Actual Times
                    </Button>
                  )}
                  <div className="flex gap-2 ml-auto">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { setEditOpen(false); setEditingShift(null); }}
                      className="border-border rounded-sm"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      data-testid="update-shift-btn"
                      disabled={updating}
                      className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm"
                    >
                      {updating ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog 
          open={deleteDialog.open} 
          onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}
        >
          <AlertDialogContent className="bg-card border-border rounded-sm" data-testid="delete-shift-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-barlow tracking-wider uppercase">
                {opsMode ? 'Request Deletion' : 'Delete this shift only'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                <span className="block">
                  {opsMode ? 'Request deletion of this shift for ' : 'Are you sure you want to delete this shift for '}
                  <strong>{deleteDialog.shift?.employee_name}</strong> on{' '}
                  <strong>{deleteDialog.shift?.date}</strong>?
                </span>
                <span className="block mt-2 text-xs text-muted-foreground">
                  {opsMode
                    ? 'The request is sent to the Primary Admin for approval. The shift stays unchanged until it is approved.'
                    : 'The employee will lose visibility of this shift in their portal. Shifts already clocked-in or completed cannot be deleted (history is preserved). This action is written to the Shift Audit Log.'}
                </span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-border rounded-sm" data-testid="delete-shift-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteShift}
                className="bg-destructive text-destructive-foreground hover:bg-red-600 rounded-sm"
                data-testid="delete-shift-confirm"
              >
                {opsMode ? 'Send deletion request' : 'Delete this shift only'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Admin Shift Time Adjustment — single-shift scope. Reused from
            the shared component so Upcoming Shifts can offer the same
            dialog for event-day / subset scopes without code duplication. */}
        <AdjustShiftTimeDialog
          open={!!adjusting}
          onOpenChange={(o) => { if (!o) setAdjusting(null); }}
          scope="shift"
          shift={adjusting}
          eventName={adjusting?.site_name || adjusting?.shift_role || ''}
          date={adjusting?.date}
          onSaved={() => { setAdjusting(null); fetchData(); }}
        />

        {/* Manual clock in/out (admin override) */}
        <ManualClockDialog
          open={manualClock.open}
          onOpenChange={(open) => setManualClock((prev) => ({ ...prev, open }))}
          employee={manualClock.employee}
          defaultAction={manualClock.action}
          shiftId={manualClock.shiftId}
          onSuccess={() => { fetchData(); fetchDayClock(); }}
        />

        {/* Delete clock punch (undo) confirmation */}
        <AdminSwapDialog
          open={swapDialog.open}
          onOpenChange={(open) => setSwapDialog((prev) => ({ ...prev, open }))}
          sourceShift={swapDialog.shift}
          category={category}
          onDone={() => { fetchData(); fetchDayClock(); }}
        />

        <Dialog open={deletePunch.open} onOpenChange={(open) => setDeletePunch((prev) => ({ ...prev, open }))}>
          <DialogContent className="bg-card border-border rounded-sm max-w-sm" data-testid="delete-punch-dialog">
            <DialogHeader>
              <DialogTitle className="font-barlow tracking-wider uppercase">
                Delete {deletePunch.event?.event_type === 'clock_out' ? 'Clock Out' : 'Clock In'}?
              </DialogTitle>
              <DialogDescription>
                {deletePunch.event?.event_type === 'clock_out'
                  ? `${deletePunch.shiftName}'s clock out will be removed — they go back On Shift and the shift returns to "in progress".`
                  : `${deletePunch.shiftName}'s clock in will be removed — the shift returns to "scheduled".`}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" className="rounded-sm" onClick={() => setDeletePunch({ open: false, event: null, shiftName: '' })} data-testid="delete-punch-cancel">
                Cancel
              </Button>
              <Button variant="destructive" className="rounded-sm" onClick={confirmDeletePunch} data-testid="delete-punch-confirm">
                <Trash2 className="w-4 h-4 mr-1.5" /> Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Clock photo viewer */}
        <Dialog open={photoDialog.open} onOpenChange={(open) => setPhotoDialog((prev) => ({ ...prev, open }))}>
          <DialogContent className="bg-card border-border rounded-sm max-w-md">
            <DialogHeader>
              <DialogTitle className="font-barlow tracking-wider uppercase">Clock Photo</DialogTitle>
            </DialogHeader>
            {photoDialog.url && (
              <img src={photoDialog.url} alt="Clock event" className="w-full rounded-sm border border-border" data-testid="clock-photo-full" />
            )}
          </DialogContent>
        </Dialog>

        {/* Map pickers — create + edit forms */}
        <MapPickerDialog
          open={createMapOpen}
          onOpenChange={setCreateMapOpen}
          initialLink={shiftForm.location_link}
          postcode={shiftForm.postcode}
          siteName={shiftForm.site_name}
          onPick={(link) => setShiftForm((f) => ({ ...f, location_link: link }))}
        />
        <MapPickerDialog
          open={editMapOpen}
          onOpenChange={setEditMapOpen}
          initialLink={editForm.location_link}
          postcode={editForm.postcode}
          siteName={editForm.site_name}
          onPick={(link) => setEditForm((f) => ({ ...f, location_link: link }))}
        />
      </div>
    </Layout>
  );
}
